import { useState } from 'react'
import omit from 'lodash/omit'
import { PERMISSION_ACTION_DENY } from '@klear/constants'
import type { OrderEventInternalNoteField } from '@klear/order-event/constants'
import { EMPTY_ORDER_EVENT_ACTION_FORM } from '@klear/order-event/wire.client'
import type { OrderEventRef } from '@klear/ui/hooks/order-event-ref'
import { Button } from '@klear/ui/components/button'
import { Checkbox } from '@klear/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { Textarea } from '@klear/ui/components/textarea'
import { useOrderActions } from '@klear/ui/hooks/use-order-actions'
import { toast } from '@klear/ui/hooks/use-toast'
import { trpc } from '@klear/ui/lib/trpc'
import { plural } from '@/app/receivables/fund-checks'

export type RejectableField = { key: OrderEventInternalNoteField; label: string }

export function RejectDialog({
  open,
  onOpenChange,
  eventRef,
  idDisplay,
  short,
  fields,
  onRejected
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventRef: OrderEventRef
  idDisplay: string
  short: string
  fields: RejectableField[]
  onRejected: () => void
}) {
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const { denyEvent } = useOrderActions()
  const utils = trpc.useUtils()
  const chosen = fields.filter((field) => field.key in notes)
  const missingNote = chosen.some((field) => (notes[field.key] ?? '').trim() === '')

  const toggle = (key: string, checked: boolean) =>
    setNotes((current) => (checked ? { ...current, [key]: current[key] ?? '' } : omit(current, key)))

  const close = (next: boolean) => {
    if (busy) return
    if (!next) setNotes({})
    onOpenChange(next)
  }

  const reject = async () => {
    setBusy(true)
    try {
      await denyEvent(eventRef.orderId, eventRef, {
        ...EMPTY_ORDER_EVENT_ACTION_FORM(PERMISSION_ACTION_DENY),
        internalNotes: chosen.map((field) => ({ field: field.key, note: notes[field.key] ?? '' }))
      })
    } catch {
      return
    } finally {
      setBusy(false)
    }
    void utils.orderEvent.listInternalNotes.invalidate({ orderEventId: eventRef.id })
    toast({ title: `${short} rejected`, description: `${plural(chosen.length, 'internal note')} saved.` })
    setNotes({})
    onRejected()
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Reject {idDisplay}?</DialogTitle>
          <DialogDescription>
            Pick what is wrong and say why. Each note is saved on the rejection for Klear only; the trader does not see
            it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-[60vh] gap-2 overflow-y-auto" role="group" aria-label="Fields to reject">
          {fields.map((field) => {
            const id = `reject-${eventRef.id}-${field.key}`
            const checked = field.key in notes
            return (
              <div key={field.key} className="grid gap-2 rounded-md border p-3">
                <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm font-medium">
                  <Checkbox
                    id={id}
                    checked={checked}
                    disabled={busy}
                    onCheckedChange={(next) => toggle(field.key, next === true)}
                  />
                  {field.label}
                </label>
                {checked ? (
                  <Textarea
                    aria-label={`Why ${field.label.toLowerCase()} is rejected`}
                    placeholder="What is wrong, and what the document shows"
                    value={notes[field.key] ?? ''}
                    disabled={busy}
                    autoFocus
                    maxLength={2000}
                    onChange={(event) => setNotes((current) => ({ ...current, [field.key]: event.target.value }))}
                  />
                ) : null}
              </div>
            )
          })}
        </div>
        <DialogFooter>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={busy || chosen.length === 0 || missingNote}
            onClick={() => void reject()}
          >
            {chosen.length > 0 ? `Reject with ${plural(chosen.length, 'note')}` : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
